import React, { useMemo } from "react";

import type { RowData } from "@tanstack/react-table-v9";

import { inColumnWindow } from "./columnWindow";
import { columnLayout, pinEdges } from "./layout";
import { isUtilityColumn } from "../../columnUtils";
import {
  computeTotal,
  formatCellValue,
  getColumnAlign,
} from "../../formatters";
import type { VirtualWindow } from "../../hooks/useGridVirtualizer";
import type { TableV2Instance } from "../../hooks/useTableV2";
import { CellBox, CellText, FooterRow } from "../../styles";

export const hasFooterRow = <TData extends RowData>(
  table: TableV2Instance<TData>,
) =>
  (table.getFooterGroups()[0]?.headers ?? []).some(
    ({ column }) =>
      column.columnDef.footer !== undefined ||
      column.columnDef.meta?.total !== undefined,
  );

/** Totals row: custom `footer`s, `meta.total` values, and the label in the first empty cell */
const GridFooter = <TData extends RowData>({
  table,
  totals,
  footerLabel,
  columnWindow,
}: {
  table: TableV2Instance<TData>;
  totals?: Record<string, unknown>;
  footerLabel: string;
  columnWindow: VirtualWindow | null;
}) => {
  const footerHeaders = table.getFooterGroups()[0]?.headers ?? [];
  const leafColumns = table.getVisibleLeafColumns();
  const fixedWidth = !!columnWindow;
  // Server pages only hold one page of rows, so totals there must come from `totals`
  const totalRows =
    table.options.rowCount === undefined
      ? table.getPrePaginatedRowModel().rows
      : undefined;
  const computedTotals = useMemo(() => {
    const result: Record<string, number | null> = {};
    if (!totalRows) return result;
    leafColumns.forEach((column) => {
      const fn = column.columnDef.meta?.total;
      if (fn) {
        result[column.id] = computeTotal(
          totalRows.map((row) => row.getValue(column.id)),
          fn,
        );
      }
    });
    return result;
  }, [totalRows, leafColumns.map((column) => column.id).join()]);

  let labelPlaced = false;
  return (
    <FooterRow role="row">
      {inColumnWindow(
        footerHeaders,
        (header) => header.column,
        columnWindow,
        (header) => {
          const { column } = header;
          const meta = column.columnDef.meta;
          const value = totals?.[column.id] ?? computedTotals[column.id];
          let content: React.ReactNode = null;
          if (column.columnDef.footer !== undefined) {
            content = <table.FlexRender footer={header} />;
          } else if (meta?.total && value !== undefined && value !== null) {
            content = formatCellValue(
              value,
              meta.total === "count" ? { type: "number" } : meta,
            );
          } else if (!labelPlaced && !isUtilityColumn(column)) {
            content = footerLabel;
            labelPlaced = true;
          }
          return (
            <CellBox
              $isHeader
              key={header.id}
              role="cell"
              $align={getColumnAlign(meta)}
              $pinned={column.getIsPinned()}
              {...pinEdges(column)}
              style={columnLayout(column, header.getSize(), false, fixedWidth)}
            >
              <CellText>{content}</CellText>
            </CellBox>
          );
        },
      )}
    </FooterRow>
  );
};

export default GridFooter;
